import { GetString } from 'fluent-react';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { semiBoldFontWeight } from '../../cssVariables';
import { PolicyRecommendation } from '../../graphQL/types/shared';
import { smallMediaQuery } from '../Grid';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import { policyColors } from '../rockSongChart/Utils';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  animation: ${fadeIn} 0.2s linear 1;
  background-color: #fff;
  height: 100%;
  grid-row: 2;
  grid-column: 1;
  display: grid;
  grid-template-rows: auto 1fr auto 1fr auto 1fr auto;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 0.7rem;
  padding: 3rem 3rem 3rem 0;
  align-items: center;
  justify-items: center;
`;

const textColorBase = '#b3b3b3';

const Text = styled.div`
  font-size: 1.1rem;
  color: ${textColorBase};
  line-height: 1.5;

  @media ${smallMediaQuery} {
    font-size: 0.7rem;
  }
`;

const iconSize = 13;
const iconSpacing = 5;

const Policy = styled(Text)`
  display: grid;
  grid-template-columns: ${iconSize + iconSpacing}px 1fr;
  align-items: start;
  height: 100%;
`;

const ArrowBase = styled.div`
  &::before {
    content: '';
    display: block;
    position: absolute;
    bottom: -5px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 11px solid transparent;
    border-top: 11px solid var(--icon-color);
    transform-origin: 4px 6px;
  }
`;

const ArrowLeft = styled(ArrowBase)`
  position: relative;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(
      to top left,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) calc(50% - var(--arrow-stroke)),
      var(--icon-color) 50%,
      rgba(0, 0, 0, 0) calc(50% + var(--arrow-stroke)),
      rgba(0, 0, 0, 0) 100%
    );

  &::before {
    left: -5px;
    transform: rotate(165deg);
  }
`;
const ArrowRight = styled(ArrowBase)`
  position: relative;
  width: 100%;
  height: 100%;
  background-image:
    linear-gradient(
      to top right,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) calc(50% - var(--arrow-stroke)),
      var(--icon-color) 50%,
      rgba(0, 0, 0, 0) calc(50% + var(--arrow-stroke)),
      rgba(0, 0, 0, 0) 100%
    );

  &::before {
    right: -11px;
    transform: rotate(65deg);
  }
`;
const ArrowTextBase = styled.div`
  white-space: nowrap;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 0.95rem;

  @media ${smallMediaQuery} {
    font-size: 0.6rem;
  }
`;
const ArrowTextLeft = styled(ArrowTextBase)`
  text-align: right;
  transform: translateX(-100%);
`;
const ArrowTextRight = styled(ArrowTextBase)`
  transform: translateX(100%);
`;

const TopLabel = styled(Text)`
  grid-column: 2 / span 2;
  grid-row: 1;
`;
const TopArrowLeft = styled(ArrowLeft)`
  grid-column: 2;
  grid-row: 2;
`;
const TopArrowRight = styled(ArrowRight)`
  grid-column: 3;
  grid-row: 2;
`;

const LightTouchLabel = styled(Policy)`
  grid-column: 1 / span 2;
  grid-row: 3;
`;
const SecondTierLabel = styled(Text)`
  grid-column: 3 / span 3;
  grid-row: 3;
`;
const SecondTierArrowLeft = styled(ArrowLeft)`
  grid-column: 3;
  grid-row: 4;
`;
const SecondTierArrowRight = styled(ArrowRight)`
  grid-column: 5;
  grid-row: 4;
`;

const ParsimoniousLabel = styled(Policy)`
  grid-column: 2 / span 2;
  grid-row: 5;
`;
const ThirdTierLabel = styled(Text)`
  grid-column: 5 / span 2;
  grid-row: 5;
`;
const ThirdTierArrowLeft = styled(ArrowLeft)`
  grid-column: 5;
  grid-row: 6;
`;
const ThirdTierArrowRight = styled(ArrowRight)`
  grid-column: 6;
  grid-row: 6;
`;

const StrategicBetsLabel = styled(Policy)`
  grid-column: 4 / span 2;
  grid-row: 7;
`;
const TechFrontierLabel = styled(Policy)`
  grid-column: 6 / span 2;
  grid-row: 7;
`;
const IconContainer = styled.span`
  width: ${iconSize}px;
  position: relative;
  top: 5px;

  polygon {
    fill: var(--icon-color);
  }
`;

const getColorHighlight = (policy: PolicyRecommendation, myPolicies: PolicyRecommendation[]) => {
  if (myPolicies.includes(policy) === true) {
    const color = policyColors[policy];
    return {
      color,
      '--icon-color': color,
      '--arrow-stroke': '2px',
      'fontWeight': semiBoldFontWeight,
    };
  } else {
    return {
      'color': textColorBase,
      '--icon-color': textColorBase,
      '--arrow-stroke': '0.8px',
    };
  }
};

const getTooltipColor = (policy: PolicyRecommendation, myPolicies: PolicyRecommendation[]) => {
  if (myPolicies.includes(policy) === true) {
    const color = policyColors[policy];
    return color;
  } else {
    return textColorBase;
  }
};

interface Props {
  getFluentString: GetString;
  policyRecommendation: PolicyRecommendation;
  countryName: string;
}

const FlowChart = (props: Props) => {
  const {
    getFluentString, policyRecommendation, countryName,
  } = props;

  return (
    <Wrapper>

      <TopLabel
        style={getColorHighlight(policyRecommendation, [
          PolicyRecommendation.LightTouch, PolicyRecommendation.ParsimoniousIndustrial,
          PolicyRecommendation.StrategicBets, PolicyRecommendation.TechFrontier,
          ])}
      >
        {getFluentString('rock-song-flow-chart-can-country-grow', { 'country-name': countryName } )}
      </TopLabel>
      <TopArrowLeft
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.LightTouch])}
      >
        <ArrowTextLeft>{getFluentString('rock-song-flow-chart-yes-label')}</ArrowTextLeft>
      </TopArrowLeft>
      <TopArrowRight
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.TechFrontier,
        PolicyRecommendation.StrategicBets, PolicyRecommendation.ParsimoniousIndustrial])}
      >
        <ArrowTextRight>{getFluentString('rock-song-flow-chart-no-label')}</ArrowTextRight>
      </TopArrowRight>

      <LightTouchLabel
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.LightTouch])}
      >
        <IconContainer
          dangerouslySetInnerHTML={{__html: require('../rockSongChart/policy.svg')}}
        />
        <span>
          <GlossaryTerm
            type={TooltipType.Policy}
            term={getFluentString('strategic-approach-name-light-touch')}
            explanation={getFluentString('strategic-approach-explanation-light-touch')}
            citation={undefined}
            align={TooltipAlign.Standard}
            color={getTooltipColor(policyRecommendation, [PolicyRecommendation.LightTouch])}
            attachment={null}
          />
        </span>
      </LightTouchLabel>
      <SecondTierLabel
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.TechFrontier,
          PolicyRecommendation.StrategicBets, PolicyRecommendation.ParsimoniousIndustrial])}
      >
        {getFluentString('rock-song-flow-chart-how-easy-is-it')}
      </SecondTierLabel>
      <SecondTierArrowLeft
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.ParsimoniousIndustrial])}
      >
        <ArrowTextLeft>{getFluentString('rock-song-flow-chart-many-nearby-jumps')}</ArrowTextLeft>
      </SecondTierArrowLeft>
      <SecondTierArrowRight
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.TechFrontier,
          PolicyRecommendation.StrategicBets])}
      >
        <ArrowTextRight>{getFluentString('rock-song-flow-chart-few-nearby-jumps')}</ArrowTextRight>
      </SecondTierArrowRight>

      <ParsimoniousLabel
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.ParsimoniousIndustrial])}
      >
        <IconContainer
          dangerouslySetInnerHTML={{__html: require('../rockSongChart/policy.svg')}}
        />
        <span>
          <GlossaryTerm
            type={TooltipType.Policy}
            term={getFluentString('strategic-approach-name-parsimonious-industrial')}
            explanation={getFluentString('strategic-approach-explanation-parsimonious-industrial')}
            citation={undefined}
            align={TooltipAlign.Standard}
            color={getTooltipColor(policyRecommendation, [PolicyRecommendation.ParsimoniousIndustrial])}
            attachment={null}
          />
        </span>
      </ParsimoniousLabel>
      <ThirdTierLabel
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.TechFrontier,
            PolicyRecommendation.StrategicBets])}
      >
        {getFluentString('rock-song-flow-chart-is-it-because-periphery-or-frontier', {
          'country-name': countryName,
        })}
      </ThirdTierLabel>
      <ThirdTierArrowLeft
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.StrategicBets])}
      >
        <ArrowTextLeft>{getFluentString('rock-song-flow-chart-periphery')}</ArrowTextLeft>
      </ThirdTierArrowLeft>
      <ThirdTierArrowRight
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.TechFrontier])}
      >
        <ArrowTextRight>{getFluentString('rock-song-flow-chart-frontier')}</ArrowTextRight>
      </ThirdTierArrowRight>

      <StrategicBetsLabel
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.StrategicBets])}
      >
        <IconContainer
          dangerouslySetInnerHTML={{__html: require('../rockSongChart/policy.svg')}}
        />
        <span>
          <GlossaryTerm
            type={TooltipType.Policy}
            term={getFluentString('strategic-approach-name-strategic-bets')}
            explanation={getFluentString('strategic-approach-explanation-strategic-bets')}
            citation={undefined}
            align={TooltipAlign.Standard}
            color={getTooltipColor(policyRecommendation, [PolicyRecommendation.StrategicBets])}
            attachment={null}
          />
        </span>
      </StrategicBetsLabel>
      <TechFrontierLabel
        style={getColorHighlight(policyRecommendation, [PolicyRecommendation.TechFrontier])}
      >
        <IconContainer
          dangerouslySetInnerHTML={{__html: require('../rockSongChart/policy.svg')}}
        />
        <span>
          <GlossaryTerm
            type={TooltipType.Policy}
            term={getFluentString('strategic-approach-name-tech-frontier')}
            explanation={getFluentString('strategic-approach-explanation-tech-frontier')}
            citation={undefined}
            align={TooltipAlign.Standard}
            color={getTooltipColor(policyRecommendation, [PolicyRecommendation.TechFrontier])}
            attachment={null}
          />
        </span>
      </TechFrontierLabel>

    </Wrapper>
  );
};

export default FlowChart;
